import { FC, useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Button, Input, Modal, Select, Space, Table, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import aiChatAdminService from "src/module/aiChatAdmin/service/aiChatAdmin.service";
import { AiChatSubject } from "src/module/aiChatAdmin/entity/aiChatAdmin.entity";

const PAGE_SIZE = 15;

export const AiSubjects: FC = () => {
	const { t } = useTranslation();
	const [list, setList] = useState<AiChatSubject[]>([]);
	const [loading, setLoading] = useState(false);
	const [keyword, setKeyword] = useState("");
	const [blocked, setBlocked] = useState<string>("all");
	const [page, setPage] = useState(1);
	const [blockTarget, setBlockTarget] = useState<AiChatSubject>();
	const [reason, setReason] = useState("");

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const result = await aiChatAdminService.listSubjects({
				q: keyword,
				blocked: blocked === "all" ? undefined : blocked === "blocked",
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			});
			setList(result.items);
		} catch {
			setList([]);
		} finally {
			setLoading(false);
		}
	}, [blocked, keyword, page]);

	useEffect(() => {
		void loadData();
	}, [loadData]);

	const blockUser = async () => {
		if (!blockTarget) return;
		await aiChatAdminService.updateSubject(blockTarget.subject_key, { blocked: true, reason });
		message.success(t("operate_success"));
		setBlockTarget(undefined);
		setReason("");
		loadData();
	};

	const unblockUser = (record: AiChatSubject) => {
		Modal.confirm({
			title: "确认解除拉黑?",
			onOk: async () => {
				await aiChatAdminService.updateSubject(record.subject_key, { blocked: false });
				message.success(t("operate_success"));
				loadData();
			},
		});
	};

	const columns = [
		{ title: "subjectKey", dataIndex: "subject_key", width: 260 },
		{ title: "类型", dataIndex: "actor_type", width: 110 },
		{ title: "用户名", dataIndex: "user_name", width: 140 },
		{ title: t("mobile"), dataIndex: "mobile", width: 130 },
		{ title: "visitorId", dataIndex: "visitor_id", width: 180 },
		{ title: "IP", dataIndex: "ip", width: 130 },
		{ title: "达到限额次数", dataIndex: "limit_hit_count", width: 130 },
		{ title: "最近限额时间", dataIndex: "last_limit_at", width: 190 },
		{
			title: "状态",
			dataIndex: "blocked",
			width: 100,
			render: (value: number) => value === 1 ? <Tag color="red">已拉黑</Tag> : <Tag color="green">正常</Tag>,
		},
		{ title: "拉黑原因", dataIndex: "blocked_reason", width: 180 },
		{ title: "更新时间", dataIndex: "updated_at", width: 190 },
		{
			title: t("action"),
			width: 140,
			fixed: "right" as const,
			render: (_value: unknown, record: AiChatSubject) => record.blocked === 1 ? (
				<Button onClick={() => unblockUser(record)}>解除</Button>
			) : (
				<Button danger onClick={() => setBlockTarget(record)}>拉黑</Button>
			),
		},
	];

	return (
		<Container>
			<Header>
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="用户名 / 手机号 / visitorId / IP"
						value={keyword}
						onChange={(event) => setKeyword(event.target.value)}
						onSearch={() => {
							setPage(1);
							loadData();
						}}
						style={{ width: 300 }}
					/>
					<Select value={blocked} onChange={(value) => { setBlocked(value); setPage(1); }} style={{ width: 120 }}>
						<Select.Option value="all">{t("all")}</Select.Option>
						<Select.Option value="normal">正常</Select.Option>
						<Select.Option value="blocked">已拉黑</Select.Option>
					</Select>
					<Button onClick={loadData}>{t("search")}</Button>
				</Space>
			</Header>
			<Table
				loading={loading}
				columns={columns}
				dataSource={list}
				rowKey={(record) => record.subject_key}
				pagination={false}
				scroll={{ x: 1900 }}
			/>
			<Pager>
				<Button disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
				<span>第 {page} 页，当前 {list.length} 条</span>
				<Button disabled={list.length < PAGE_SIZE} onClick={() => setPage(page + 1)}>下一页</Button>
			</Pager>
			<Modal
				title="拉黑用户"
				open={!!blockTarget}
				onOk={blockUser}
				onCancel={() => {
					setBlockTarget(undefined);
					setReason("");
				}}
			>
				<p>{blockTarget?.subject_key}</p>
				<Input.TextArea
					rows={4}
					placeholder="拉黑原因，便于后续复核"
					value={reason}
					onChange={(event) => setReason(event.target.value)}
				/>
			</Modal>
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	padding: 20px;
`;

const Header = styled.div`
	margin-bottom: 16px;
`;

const Pager = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 16px;
`;
